import {
  MAX_TRACE_TICKS,
  SIM_VERSION,
  simulate,
  traceBattle,
  type BattleResult,
  type Frame,
  type Layout,
} from "@empire/core-sim";
import type { Preset } from "./presets.js";

/**
 * One test run of a maze against one preset army (S2-5).
 *
 * The run is FREE and unlimited, and it uses exactly the same `simulate` the
 * server uses to resolve a real raid. So the answer here is not an estimate or
 * a model of the battle — it IS the battle, minus the consequences.
 */

export interface DryRun {
  readonly presetId: string;
  readonly result: BattleResult;
  /** Per-tick positions and effects, for the mini replay. Empty when not asked for. */
  readonly frames: readonly Frame[];
}

export function dryRun(layout: Layout, preset: Preset, trace = true): DryRun {
  const input = { simVersion: SIM_VERSION, layout, plan: preset.plan };

  // The tracer lives in core-sim next to the simulation it watches, so the
  // editor's mini replay, the real replay viewer and the server's recording are
  // one piece of code. Three copies of this loop is three chances for the same
  // battle to be drawn two different ways.
  if (!trace) return { presetId: preset.id, result: simulate(input), frames: [] };

  const { result, frames } = traceBattle(input, MAX_TRACE_TICKS);
  return { presetId: preset.id, result, frames };
}

// Re-exported so the editor, the worker and the tests keep importing "what a
// dry run produces" from the dry run, rather than each reaching into core-sim
// for a type they only use because a dry run hands it to them.
export { MAX_TRACE_TICKS };
export type { Frame };
